import { Controller } from "@hotwired/stimulus"

// Live "starts in" countdown for meeting rows. Timed meetings
// (`at` = ISO timestamp) tick every second showing days / hours /
// minutes / seconds (largest non-zero unit down to seconds, e.g.
// "2d 5h 30m 12s", "5h 30m 12s", "42m 09s", "08s"). Date-only meetings
// (`date` = YYYY-MM-DD) have no time, so they count whole calendar days.
// Past or missing targets render nothing.
export default class extends Controller {
  static values = { at: String, date: String }

  connect() {
    this._render()
    // Tick every second so the seconds count stays live.
    this._timer = setInterval(() => this._render(), 1000)
  }

  disconnect() {
    if (this._timer) clearInterval(this._timer)
  }

  _render() {
    const label = this._label()
    if (!label) {
      this.element.innerHTML = ""
      // Fire once when a future countdown reaches zero, so a meeting row can
      // swap its "Scheduled" chip for "Happening now" without a refresh. Rows
      // already past at load never counted, so they never fire.
      if (this._wasCounting && !this._completed) {
        this._completed = true
        this.dispatch("complete", { bubbles: true })
      }
      return
    }
    this._wasCounting = true
    // Less than a day away → amber to draw the eye; further out → gray.
    const hasDays = /\d+\s*d\b/.test(label) || /days?\b/.test(label) || label === "Tomorrow"
    const cls = hasDays ? "bg-gray-100 text-gray-600" : "bg-amber-100 text-amber-800"
    this.element.innerHTML =
      `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium tabular-nums ${cls}">${label}</span>`
  }

  _label() {
    if (this.hasAtValue && this.atValue) {
      const ms = new Date(this.atValue).getTime()
      if (Number.isNaN(ms)) return ""
      return this._fromTimestamp(ms)
    }
    if (this.hasDateValue && this.dateValue) {
      return this._fromDate(this.dateValue)
    }
    return ""
  }

  _fromTimestamp(targetMs) {
    const diff = targetMs - Date.now()
    // Once the start time has passed the countdown is over — the
    // "Happening now" badge (or the status) tells the user what's going
    // on; the column stays blank rather than ticking negative.
    if (diff <= 0) return ""

    const total = Math.floor(diff / 1000)
    const sec = total % 60
    const min = Math.floor(total / 60) % 60
    const hr = Math.floor(total / 3600) % 24
    const day = Math.floor(total / 86400)
    const p = (n) => String(n).padStart(2, "0")

    if (day >= 1) return `${day}d ${p(hr)}h ${p(min)}m ${p(sec)}s`
    if (hr >= 1) return `${hr}h ${p(min)}m ${p(sec)}s`
    if (min >= 1) return `${min}m ${p(sec)}s`
    return `${sec}s`
  }

  _fromDate(ymd) {
    const parts = String(ymd).split("-").map(Number)
    if (parts.length < 3 || parts.some(Number.isNaN)) return ""
    const target = new Date(parts[0], parts[1] - 1, parts[2])
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const days = Math.round((target - today) / 86400000)
    if (days <= 0) return ""
    if (days === 1) return "Tomorrow"
    return `in ${days} days`
  }
}
